import { useQuery } from '@tanstack/react-query';
import { TopChatAgent, TopChatAgentResult } from '../../interfaces/Analytics';
import { getTopChatAgents } from '../../services/dashboardService';
import { ChatAgent } from '../../interfaces/ChatAgent';

interface UseTopAgentsParams {
  companyId: string | undefined;
  userId?: string;
  startDate: string | undefined;
  endDate: string | undefined;
  agentsList: ChatAgent[] | undefined;
  enabled?: boolean;
}

const remapTopChatAgents = (
  topAgentsResult: TopChatAgentResult[],
  agentsList: ChatAgent[]
): TopChatAgent[] => {
  return topAgentsResult.map(agent => {
    const agentDetails = agentsList.find(
      agentsListItem => agentsListItem.id === agent.chat_agent_id
    );
    return {
      agentName: agentDetails?.name ?? '',
      image: agentDetails?.custom_initialization?.header_avatar_url ?? '',
      conversations: agent.total_conversations,
      messages: agent.total_messages,
      averageSatisfaction: agent.average_user_satisfaction,
    };
  });
};

export const useTopChatAgents = ({
  companyId,
  userId,
  startDate,
  endDate,
  agentsList,
  enabled = true,
}: UseTopAgentsParams) => {
  return useQuery<TopChatAgent[], Error>({
    queryKey: ['top-chat-agents', companyId, userId, startDate, endDate],
    queryFn: async () => {
      if (!companyId || !startDate || !endDate) {
        return [];
      }
      const topChatAgentsData = await getTopChatAgents(
        companyId,
        startDate,
        endDate,
        userId
      );
      const topChatAgentsResult = topChatAgentsData.message.top_chat_agents;

      if (!agentsList || agentsList.length === 0) {
        return [];
      }

      return remapTopChatAgents(topChatAgentsResult, agentsList);
    },
    enabled:
      enabled &&
      !!companyId &&
      !!startDate &&
      !!endDate &&
      !!agentsList &&
      agentsList.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnMount: false, // Don't refetch when navigating between pages
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnReconnect: false, // Don't refetch when reconnecting
  });
};
